/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Navigation Store
 *
 * Manages navigation state with persistence to localStorage.
 * Tracks expanded sections and sidebar collapsed state.
 */

import type { NavigationState } from "@/types/navigation";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Default sections to expand on first load
 * Based on most commonly accessed areas
 */
const defaultExpandedSections: string[] = [];

/**
 * Navigation state store with localStorage persistence
 */
export const useNavigationStore = create<NavigationState>()(
  persist(
    (set) => ({
      // State
      expandedSections: defaultExpandedSections,
      sidebarCollapsed: false as boolean,
      accordionMode: true as boolean, // Enable accordion mode by default (only one section open)
      autoCollapseOnNavigate: false as boolean, // Disabled by default for better UX
      lastManualToggle: 0, // Timestamp of last manual toggle to prevent auto-expand interference

      // Actions
      toggleSection: (sectionId: string) =>
        set((state) => {
          const isExpanded = state.expandedSections.includes(sectionId);

          if (state.accordionMode) {
            // In accordion mode: allow switching between sections
            if (isExpanded) {
              // If clicking on the currently expanded section, close it
              return {
                expandedSections: [],
                lastManualToggle: Date.now(),
              };
            } else {
              // If clicking on a different section, open it (closes any others automatically)
              return {
                expandedSections: [sectionId],
                lastManualToggle: Date.now(),
              };
            }
          } else {
            // Normal mode: toggle individual section
            return {
              expandedSections: isExpanded
                ? state.expandedSections.filter((id) => id !== sectionId)
                : [...state.expandedSections, sectionId],
              lastManualToggle: Date.now(),
            };
          }
        }),

      toggleSidebar: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      expandSection: (sectionId: string) =>
        set((state) => {
          if (state.accordionMode) {
            // In accordion mode: close all others and open this one
            return {
              expandedSections: [sectionId],
            };
          } else {
            // Normal mode: add to expanded sections if not already there
            return {
              expandedSections: state.expandedSections.includes(sectionId)
                ? state.expandedSections
                : [...state.expandedSections, sectionId],
            };
          }
        }),

      collapseSection: (sectionId: string) =>
        set((state) => ({
          expandedSections: state.expandedSections.filter(
            (id) => id !== sectionId,
          ),
        })),

      collapseAllSections: () =>
        set({
          expandedSections: [],
        }),

      setAccordionMode: (enabled: boolean) =>
        set((state) => {
          // When enabling accordion mode, keep only the first expanded section
          const firstSection = state.expandedSections[0];
          const newExpandedSections =
            enabled && state.expandedSections.length > 0 && firstSection
              ? [firstSection]
              : state.expandedSections;

          return {
            accordionMode: enabled,
            expandedSections: newExpandedSections,
          };
        }),

      setAutoCollapseOnNavigate: (enabled: boolean) =>
        set({
          autoCollapseOnNavigate: enabled,
        }),

      // Navigate to page and ensure section is expanded
      navigateToPage: (sectionId: string) =>
        set((state) => {
          if (state.accordionMode) {
            // In accordion mode: ensure this section is the only one expanded
            return {
              expandedSections: [sectionId],
            };
          } else {
            // Normal mode: add to expanded sections if not already there
            return {
              expandedSections: state.expandedSections.includes(sectionId)
                ? state.expandedSections
                : [...state.expandedSections, sectionId],
            };
          }
        }),

      reset: () =>
        set({
          expandedSections: defaultExpandedSections,
          sidebarCollapsed: false,
          accordionMode: true,
          autoCollapseOnNavigate: false,
          lastManualToggle: 0,
        }),
    }),
    {
      name: "navigation-state",
      partialize: (state) => ({
        expandedSections: state.expandedSections,
        sidebarCollapsed: state.sidebarCollapsed,
        accordionMode: state.accordionMode,
        autoCollapseOnNavigate: state.autoCollapseOnNavigate,
        // Don't persist lastManualToggle - it's a temporary flag
      }),
    },
  ),
);
